// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { isValid } from 'date-fns'
import { Box, Button, DialogActions, Divider, Grid, IconButton, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
import { EditExternalUserAddress } from './EditExternalUserAddress'
import { InputFormFields } from './InputFormFields'
// others
import {
  CaseStatus,
  ExternalUser,
  Maybe,
  UpdateExternalUserInput,
  useUpdateExternalUserMutation
} from '../../../generated'
import { ADVERSARY_ADDRESS, CANCEL, GENERAL_INFORMATION } from '../../constants'
import { CaseContext } from '../../context'
import { AppContext } from '../../context/AppContext'
import { ActionType } from '../../context/AppContextReducer'
import { EditExternalUserModalProps } from '../../interfaceTypes'
import { returnErrorMessage } from '../../utils'
import { updateJudgeValidationSchema } from '../../validationSchema'
import { CommonExternalUserView } from '../common/CommonExternalUserView'
import { ExternalUserActionType } from './externalUserReducer'

interface EditAdversaryProps extends EditExternalUserModalProps {
  modalTitle?: string
  successMessage?: string
  isEdit?: boolean
}
export const EditAdversary: FC<EditAdversaryProps> = ({
  refetch,
  contactRefetch: singleExternalRefetch,
  userDetails,
  caseRefetch,
  dispatch: externalUserDispatch,
  state,
  modalTitle,
  successMessage,
  isEdit: defaultIsEdit
}): JSX.Element => {
  const { dispatch, isEdit } = useContext(AppContext)
  const { currentCase } = useContext(CaseContext)
  const { status: caseStatus } = currentCase || {}
  const isClosed = caseStatus === CaseStatus.Completed
  const [isEditOpen, setIsEditOpen] = useState<boolean>(defaultIsEdit ?? false)
  const userDetailTest = userDetails && userDetails
  const [userDetail, setUserDetail] = useState<Maybe<ExternalUser>>(userDetailTest!)
  const {
    firstName,
    middleName,
    lastName,
    businessName,
    initials,
    suffix,
    dateOfBirth,
    email,
    phoneNumber,
    homePhone,
    personalPhone,
    addresses,
    id,
    status
  } = userDetail || {}
  const { id: addressId } = (addresses && addresses[0]) || {}
  const phoneNumberWithOutCountryCode = phoneNumber?.at(0) === '+' ? phoneNumber?.substring(2) : phoneNumber

  useEffect(() => {
    setUserDetail(userDetailTest!)
    // eslint-disable-next-line
  }, [userDetails])

  const handleClose = () => {
    isEdit && dispatch({ type: ActionType.SET_IS_EDIT, isEdit: !isEdit })
    refetch && refetch()
    singleExternalRefetch && singleExternalRefetch()
    caseRefetch && caseRefetch()
    setIsEditOpen(false)
  }

  const [updateExternalUserMutation, { loading: updateExternalUserLoader }] = useUpdateExternalUserMutation({
    onError({ message }) {
      returnErrorMessage(message)
    },

    onCompleted(data) {
      if (data) {
        const {
          updateExternalUser: { response, externalUser }
        } = data || {}

        if (response) {
          const { status } = response

          if (status === 200 && externalUser) {
            Alert.success(successMessage ?? 'Adversary Updated Successfully')
            externalUserDispatch &&
              externalUserDispatch({
                type: ExternalUserActionType.SET_CURRENT_EXTERNAL_USER,
                currentExternalUser: externalUser
              })
            setUserDetail(externalUser)
            resetFields()
            setIsEditOpen(false)
          }
        }
      }
    }
  })

  const methods = useForm<UpdateExternalUserInput>({
    mode: 'all',
    resolver: yupResolver(updateJudgeValidationSchema)
  })

  const { handleSubmit, reset } = methods
  const resetFields = useCallback(() => {
    reset({
      firstName: firstName || '',
      middleName: middleName || '',
      suffix: suffix || '',
      initials: initials || '',
      dateOfBirth: dateOfBirth || '',
      lastName: lastName || '',
      businessName: businessName || '',
      phoneNumber: phoneNumberWithOutCountryCode || '',
      homePhone: homePhone || '',
      personalPhone: personalPhone || '',
      email: email || ''
    })
  }, [
    businessName,
    dateOfBirth,
    email,
    firstName,
    homePhone,
    initials,
    lastName,
    middleName,
    personalPhone,
    phoneNumberWithOutCountryCode,
    reset,
    suffix
  ])

  useEffect(() => {
    resetFields()
  }, [firstName, lastName, status, phoneNumber, email, phoneNumberWithOutCountryCode, resetFields])

  const onSubmit: SubmitHandler<UpdateExternalUserInput> = async (data: UpdateExternalUserInput) => {
    const { firstName, lastName, phoneNumber, homePhone, personalPhone, dateOfBirth, ...rest } = data

    await updateExternalUserMutation({
      variables: {
        updateExternalUserInput: {
          ...rest,
          lastPayDate: undefined,
          id: id || '',
          firstName: firstName?.trim() || '',
          lastName: lastName?.trim() || '',
          phoneNumber: phoneNumber ?? undefined,
          homePhone: homePhone ?? undefined,
          personalPhone: personalPhone ?? undefined,
          dateOfBirth: isValid(new Date(dateOfBirth)) ? dateOfBirth : undefined
        }
      }
    })
  }

  return (
    <CustomModal title={modalTitle ?? 'Adversary Details'} isOpen={isEdit} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" style={{ alignItems: 'baseline' }}>
            <Typography variant="body2" fontSize="18px" sx={{ fontWeight: 'medium', marginBottom: '0.875rem' }}>
              {GENERAL_INFORMATION}
            </Typography>

            <Box style={{ alignItems: 'baseline' }}>
              <IconButton
                sx={{ p: 0 }}
                color="primary"
                size="large"
                onClick={() => {
                  setIsEditOpen(!isEditOpen)
                  resetFields()
                }}
                aria-label={isEditOpen ? 'back' : 'edit'}
                disabled={isClosed}
              >
                {isEditOpen ? <ArrowBackIcon /> : <EditIcon />}
              </IconButton>

              {isEditOpen && (
                <IconButton color="primary" type="submit" disabled={updateExternalUserLoader} aria-label="save">
                  <SaveIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box marginTop={2} marginBottom={3}>
            <Collapse in={!isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <CommonExternalUserView externalUser={userDetail as ExternalUser} />
              </Grid>
            </Collapse>

            <Collapse in={isEditOpen}>
              <Grid container spacing={2} rowSpacing={2}>
                <InputFormFields isClient={modalTitle?.includes('Contact')} />
              </Grid>
            </Collapse>
          </Box>
        </form>
      </FormProvider>
      <Divider />

      <Box marginTop={2}>
        <EditExternalUserAddress
          externalUserId={id || ''}
          addressId={addressId || ''}
          addresses={addresses || []}
          refetch={refetch}
          resetFields={resetFields}
          externalUserTitle={modalTitle?.includes('Contact') ? 'Contacts Address' : ADVERSARY_ADDRESS}
          handleClose={handleClose}
          externalUserDispatch={externalUserDispatch}
          externalUserState={state}
        />
      </Box>

      <DialogActions sx={{ padding: '30px 0 0' }}>
        <Button color="primary" variant="contained" onClick={handleClose} aria-label={CANCEL}>
          {CANCEL}
        </Button>
      </DialogActions>
    </CustomModal>
  )
}
